"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CoreMaterialCategories() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const experiences = [{
    number: "01",
    title: "Ceiling & Drywall Steel Profiles",
    description: "Precision-rolled galvanized steel sections including C-Stud, U-Track, and Furring channels for high-performance partition systems.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_profiles_drywall.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_profiles_drywall.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_profiles_drywall.png-wvc-sizes",
    alt: "Industrial galvanized steel profiles for drywall construction",
    link: "/MaterialCatalogPage"
  }, {
    number: "02",
    title: "Architectural GI & Aluminum Shadow Gaps",
    description: "Specialized perimeter profiles designed for seamless aesthetic transitions and recessed lighting integration in modern interiors.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_shadow_gaps.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_shadow_gaps.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_shadow_gaps.webp-wvc-sizes",
    alt: "Architectural aluminum shadow gap profiles",
    link: "/MaterialCatalogPage"
  }, {
    number: "03",
    title: "Fasteners & Fixing Hardware",
    description: "Heavy-duty mechanical fixings, self-drilling screws, and structural anchors engineered for maximum load-bearing reliability.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/fasteners_fixing_hardware.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/fasteners_fixing_hardware.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/fasteners_fixing_hardware.png-wvc-sizes",
    alt: "Assorted industrial fasteners and fixing hardware",
    link: "/MaterialCatalogPage"
  }];
  return <section data-section-id="4486" id="core-material-categories" className="relative bg-background text-foreground py-32 md:py-44 lg:py-[180px] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className={`text-center mb-20 md:mb-28 transition-all duration-[900ms] ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] mb-8 text-chart-3">
            Our Core Material Categories
          </p>
          <h2 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[72px] leading-[1.05] tracking-tighter max-w-[18ch] mx-auto">
            Engineered for structural excellence.
          </h2>
        </div>

        <div ref={gridRef} className={`relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 transition-all duration-[900ms] ease-out delay-150 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {experiences.map((exp, i) => <div key={exp.number} data-index={i} className="group relative p-6 md:p-8 border border-accent-metallic transition-colors duration-500 hover:border-primary flex flex-col h-full">
              <div className="relative overflow-hidden mb-8 aspect-[4/3]">
                <img src={exp.image} data-wvc-srcset={exp.srcset} data-wvc-sizes={exp.sizes} alt={exp.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.04]" />
              </div>

              <div className="font-mono text-sm tracking-[0.2em] mb-4 text-chart-3">
                {exp.number}
              </div>

              <h3 className="font-default font-semibold text-2xl md:text-3xl leading-tight mb-4 transition-transform duration-500 ease-out group-hover:translate-x-1.5">
                {exp.title}
              </h3>

              <p className="font-mono text-sm leading-relaxed mb-8 text-chart-5 flex-grow">
                {exp.description}
              </p>

              <Link to={exp.link} className="inline-flex items-center gap-2 font-mono text-sm tracking-wider uppercase pb-1 border-b transition-all duration-500 text-chart-3 border-chart-3 w-fit">
                <span className="relative">
                  View Details
                </span>
                <ArrowUpRight className="size-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>)}
        </div>

        <div ref={ctaRef} className={`mt-20 md:mt-28 text-center transition-all duration-[900ms] ease-out delay-300 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Link to="/MaterialCatalogPage" className="inline-flex items-center gap-2 font-mono text-xs md:text-sm uppercase tracking-[0.25em] pb-1 border-b transition-opacity duration-300 hover:opacity-80 text-chart-3 border-chart-3">
            Browse full catalog
            <ArrowUpRight className="size-3.5" />
          </Link>
        </div>
      </div>
    </section>;
}